import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Archive, ArrowLeft, Bell, BellOff, Sun } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useChatRooms, useSetRoomPinned } from "../api/chatApi";
import { ChatRoomList } from "../components/ChatRoomList";
import { ChatRoomView } from "../components/ChatRoomView";
import { NewRoomDialog } from "../components/NewRoomDialog";
import { useWebPush } from "../hooks/useWebPush";
import { PushErrorNotice } from "../components/PushErrorNotice";

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  useEffect(() => {
    function onResize() {
      setMobile(window.innerWidth < 640);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

export function GGPage() {
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isCeo = !!user?.is_ceo;
  const isMobile = useIsMobile();

  const [view, setView] = useState<"active" | "archive">("active");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Message id to scroll to after opening a room from a global-search hit.
  const [jumpMessageId, setJumpMessageId] = useState<string | null>(null);

  const active = useChatRooms({ archivedOnly: false });
  // Only while the archive is on screen — it is a director-only view. Mounted
  // unconditionally, every refresh of the room list (each realtime nudge) served
  // the most expensive list in the app twice, and for anyone but a director the
  // second answer was the same active list (the backend ignores the flag).
  const archived = useChatRooms({ archivedOnly: true, enabled: isDirector && view === "archive" });
  const setPinned = useSetRoomPinned();

  const rooms = useMemo(
    () => (view === "archive" ? archived.data ?? [] : active.data ?? []),
    [view, active.data, archived.data],
  );
  const isLoading = view === "archive" ? archived.isLoading : active.isLoading;

  // Switching between active/archive drops the current selection — but not on the
  // first render, where it would silently wipe a room opened via the ?room= deep
  // link below. Relying on effect declaration order for that is too fragile.
  const firstViewRender = useRef(true);
  useEffect(() => {
    if (firstViewRender.current) {
      firstViewRender.current = false;
      return;
    }
    setSelectedId(null);
  }, [view]);

  // Deep link from a push notification click: /chat?room=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const room = searchParams.get("room");
    if (room) {
      setSelectedId(room);
      const next = new URLSearchParams(searchParams);
      next.delete("room");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const push = useWebPush();
  const pushDenied = push.permission === "denied";

  // On mobile: show either the list OR the room, never both.
  const showList = !isMobile || !selectedId;
  const showRoom = !isMobile || !!selectedId;

  return (
    // Height is derived from <main>'s box (padding compensated by the negative
    // margins), NOT from 100vh: the layout is 100dvh minus the top bar and any
    // maintenance/timesheet banners, and on Brave/mobile 100vh > 100dvh. Any
    // mismatch made <main> scroll on top of the message list (double scroll).
    <div className="-m-4 lg:-m-6 h-[calc(100%+2rem)] lg:h-[calc(100%+3rem)] bg-white text-gray-900 flex flex-col overflow-hidden">
      {/* Only show the page header when the room list is on screen */}
      {showList && (
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-3 py-2 sm:px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center bg-amber-400 text-white">
              <Sun size={17} />
            </span>
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 font-mono text-sm font-bold uppercase tracking-[0.14em] text-gray-900">
                GG
                {view === "archive" && (
                  <span className="text-[11px] font-normal normal-case tracking-normal text-gray-500">
                    / Zarchiwizowane
                  </span>
                )}
              </h1>
              <p className="text-[11px] text-gray-500">Komunikator wewnętrzny</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
          {push.available && (
            <button
              type="button"
              disabled={push.busy}
              onClick={() => (push.subscribed ? push.disable() : push.enable())}
              title={
                pushDenied
                  ? "Powiadomienia zablokowane — odblokuj klikając ikonę kłódki w pasku adresu przeglądarki"
                  : push.error ??
                    (push.subscribed
                      ? "Wyłącz powiadomienia push"
                      : "Włącz powiadomienia push")
              }
              className={`text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 ${
                push.subscribed
                  ? "text-amber-500 hover:text-amber-600"
                  : pushDenied
                    ? "text-red-400 hover:text-red-600"
                    : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {push.subscribed ? <Bell size={14} /> : <BellOff size={14} />}
            </button>
          )}
          {isDirector && (
            <button
              type="button"
              onClick={() =>
                setView(view === "archive" ? "active" : "archive")
              }
              className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
            >
              {view === "archive" ? (
                <>
                  <ArrowLeft size={14} /> Aktywne
                </>
              ) : (
                <>
                  <Archive size={14} /> Zarchiwizowane
                </>
              )}
            </button>
          )}
          </div>
        </div>
      )}
      {push.error && (
        <PushErrorNotice message={push.error} onClose={push.clearError} />
      )}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {showList && (
          <ChatRoomList
            rooms={rooms}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onOpenMessage={(roomId, messageId) => {
              setSelectedId(roomId);
              setJumpMessageId(messageId);
            }}
            onNew={view === "archive" ? undefined : () => setDialogOpen(true)}
            onTogglePin={
              view === "archive"
                ? undefined
                : (room) =>
                    setPinned.mutate({ roomId: room.id, pinned: !room.is_pinned })
            }
            isLoading={isLoading}
            currentUserId={user?.id}
            variant={view}
            fullWidthOnMobile={isMobile}
          />
        )}
        {showRoom &&
          (selectedId ? (
            <ChatRoomView
              // Forcing a remount on room change resets every internal scroll
              // tracker — without it the first paint of a freshly-selected
              // room sometimes lands mid-scroll because the pin observers
              // were already set up against the previous content height.
              key={selectedId}
              roomId={selectedId}
              currentUserId={user?.id}
              isDirector={isDirector}
              isCeo={isCeo}
              isArchivedView={view === "archive"}
              showBack={isMobile}
              onBack={() => setSelectedId(null)}
              onRoomGone={() => setSelectedId(null)}
              jumpToMessageId={jumpMessageId}
              onJumpConsumed={() => setJumpMessageId(null)}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[#f2f2f2] px-6 text-center">
              <span className="flex h-11 w-11 items-center justify-center border border-gray-300 bg-white text-amber-400">
                <Sun size={22} />
              </span>
              <p className="text-sm text-gray-500">
                {view === "archive"
                  ? "Wybierz zarchiwizowaną rozmowę z listy."
                  : "Wybierz rozmowę z listy lub utwórz nową."}
              </p>
            </div>
          ))}
      </div>
      <NewRoomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(roomId) => {
          setSelectedId(roomId);
          setDialogOpen(false);
        }}
      />
    </div>
  );
}
